import { useParams, Link } from "react-router-dom";
import SEO from "@/components/feature/SEO";
import Navbar from "@/components/base/Navbar";
import Footer from "@/components/base/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">您访问的文章不存在或已被移除。</p>
            <Link
              to="/"
              className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const pageTitle = `${article.title} - jm下载指南`;
  const canonicalUrl = `https://www.jmxiazai.com/article/${article.slug}`;

  const seoMeta = {
    title: pageTitle,
    description: article.description,
    keywords: article.keywords.join(","),
    canonical: canonicalUrl,
    ogTitle: pageTitle,
    ogDescription: article.description,
    ogUrl: canonicalUrl,
    ogType: "article",
    ogLocale: "zh_CN",
    twitterCard: "summary_large_image",
    twitterTitle: pageTitle,
    twitterDescription: article.description,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.description,
        author: { "@type": "Person", name: article.author },
        datePublished: article.date,
        publisher: { "@type": "Organization", name: "jm下载指南" },
        mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://www.jmxiazai.com" },
          { "@type": "ListItem", position: 2, name: article.categoryName, item: `https://www.jmxiazai.com/category/${article.category}` },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <SEO meta={seoMeta} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.description}
              </p>
              <div className="flex items-center gap-2 mt-5">
                <span className="text-xs text-foreground-400">作者：{article.author}</span>
              </div>
            </header>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-800
                prose-headings:text-foreground-950 prose-headings:font-bold
                prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
                prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground-700
                prose-li:leading-relaxed prose-li:text-foreground-700
                prose-strong:text-foreground-900
                prose-ol:pl-5 prose-ul:pl-5
                prose-table:border prose-table:border-collapse
                prose-th:border prose-th:border-background-300 prose-th:p-2 prose-th:bg-background-100 prose-th:text-sm
                prose-td:border prose-td:border-background-300 prose-td:p-2 prose-td:text-sm"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.keywords.map((kw) => (
                  <span
                    key={kw}
                    className="px-3 py-1 bg-background-100 text-foreground-600 rounded-full text-xs whitespace-nowrap"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    description={ra.description}
                    categoryName={ra.categoryName}
                    date={ra.date}
                    readTime={ra.readTime}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}